import React from 'react';

import { Path, UseFormReturn } from 'react-hook-form';

import { TSelectChangeHandler } from 'components/shared/BaseFormComponents/Select/types';

import { IEventSelectOption } from '../types';
import { PageFieldValues, PendingPage } from '../useDefaultValues/types';

type Props<T extends PageFieldValues> = {
  keepData: (page: NonNullable<T['attachedPage']>) => void;
  resetData: () => void;
  setProceedStatus: (status: 'resetData' | 'keepData') => void;
  handleAttachedPage: TSelectChangeHandler;
  methods: UseFormReturn<T>;
  prevAttachedPage?: IEventSelectOption;
  getSavedDefaultValues?: (page: PendingPage) => Promise<T | undefined>;
};

export function useChangePagePopover<T extends PageFieldValues>({
  methods,
  keepData,
  resetData,
  setProceedStatus,
  handleAttachedPage,
  prevAttachedPage,
  getSavedDefaultValues,
}: Props<T>) {
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const { getValues } = methods;
  const attachedPage = getValues('attachedPage' as Path<T>);

  const handleResetData = () => {
    setProceedStatus('resetData');
  };

  const onChangePage: TSelectChangeHandler = async (name, id, config) => {
    let hasSavedDefaultValues = false;
    const isNewAttachedPage = attachedPage?.isNew; // used attachedPage because it's setted in initial form values

    const isAttachedPageExists = attachedPage?.id || attachedPage?.name;
    const isAttachedPageSame = attachedPage?.id === id && attachedPage?.name === name; // check current inputed page

    const isPrevAttachedPageExists = prevAttachedPage?.id || prevAttachedPage?.name;
    const isPrevPageSame = prevAttachedPage?.id === id && prevAttachedPage?.name === name;

    handleAttachedPage(name, id, config);

    /* check saved default values for flight & transfer because we should save filled data if no saved data exists */
    if (getSavedDefaultValues) {
      const res = await getSavedDefaultValues({ id, name });
      hasSavedDefaultValues = Boolean(res);
    }

    if (!name && !id) return;

    if (isNewAttachedPage) {
      /* if page isNew we need to check prevAttachedPage (saved value) */
      if (isPrevPageSame) return;
      if (!isPrevAttachedPageExists) return handleResetData();
    } else {
      /* if page !isNew we need to check attachedPage (current input value) */
      if (!isAttachedPageExists) {
        /* if page is not exists we need to check saved default values for flight & transfer */
        if (getSavedDefaultValues && !hasSavedDefaultValues) return;

        return handleResetData();
      }
      if (isAttachedPageSame) return;
    }

    if (!popoverRef?.current) return;

    /* Open the page switch modal */
    setIsPopoverOpen(true);
  };

  return {
    onChangePage,
    popover: {
      ref: popoverRef,
      isOpen: isPopoverOpen,
      onClose: () => {
        attachedPage && keepData(attachedPage);
        setIsPopoverOpen(false);
      },
      onKeep: () => {
        attachedPage && keepData(attachedPage);
        setIsPopoverOpen(false);
      },
      onReset: () => {
        resetData();
        setIsPopoverOpen(false);
      },
    },
  };
}
